import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useFormikContext } from 'formik';

import { Grid, Typography } from '@mui/material';

import { TranslationKeys } from 'app/translations';

import format from 'app/utils/formatter';

import { useDialog } from 'app/hooks';

import { InputFieldFactory } from 'app/components/FormikField';
import type { GetInputOptionsAndLabelFunc } from 'app/components';
import { FormikModal, SubmitConfirmationDialog } from 'app/components';

import type { EmployeeEmploymentScheduleFormValues } from 'app/Domain/Employees/Pages/EmployeeDetailPage/components/EmployeeEmploymentTab/components/EmployeeEmploymentSchedules/EmployeeEmploymentSchedules.types';
import {
  EmployeeEmploymentScheduleFieldOptionsAndLabel,
  EmployeeEmploymentScheduleFields,
} from 'app/Domain/Employees/Pages/EmployeeDetailPage/components/EmployeeEmploymentTab/components/EmployeeEmploymentSchedules/EmployeeEmploymentSchedules.types';

type EmployeeEmploymentScheduleFormDialogBodyProps = {
  open: boolean;
  isEdit: boolean;
  closeDialog: () => void;
};

export const EmployeeEmploymentScheduleFormDialogBody = ({
  open,
  isEdit,
  closeDialog,
}: EmployeeEmploymentScheduleFormDialogBodyProps) => {
  const { initialValues, values, submitForm, resetForm, setFieldValue } =
    useFormikContext<EmployeeEmploymentScheduleFormValues>();

  const {
    dialogState: submitConfirmationDialogState,
    openDialog: openSubmitConfirmationDialog,
    closeDialog: closeSubmitConfirmationDialog,
  } = useDialog();

  React.useEffect(() => {
    const fullTimeHoursPerWeek = parseFloat(`${values.fullTimeHoursPerWeek}`) || 0;
    const partTimePercentage = parseFloat(`${values.partTimePercentage}`) || 0;
    const computedHoursPerWeek = Math.round(fullTimeHoursPerWeek * (partTimePercentage / 100) * 100) / 100;

    setFieldValue(EmployeeEmploymentScheduleFields.HoursPerWeek.InputName, computedHoursPerWeek, false);
  }, [values.fullTimeHoursPerWeek, values.partTimePercentage, setFieldValue]);

  const onClickSubmit = React.useCallback(() => {
    const hasChangedValues = JSON.stringify(initialValues) !== JSON.stringify(values);

    if (!isEdit) {
      submitForm();
    } else if (hasChangedValues) {
      openSubmitConfirmationDialog();
    } else {
      closeDialog();
    }
  }, [closeDialog, initialValues, isEdit, openSubmitConfirmationDialog, submitForm, values]);

  const submitFormAfterConfirmation = React.useCallback(() => {
    closeSubmitConfirmationDialog();
    submitForm();
  }, [closeSubmitConfirmationDialog, submitForm]);

  const getInputOptionsAndLabel = React.useCallback<GetInputOptionsAndLabelFunc>(inputName => {
    return EmployeeEmploymentScheduleFieldOptionsAndLabel[inputName];
  }, []);

  return (
    <>
      {submitConfirmationDialogState && (
        <SubmitConfirmationDialog
          open={submitConfirmationDialogState}
          onClose={closeSubmitConfirmationDialog}
          onConfirm={submitFormAfterConfirmation}
          previousValues={initialValues}
          newValues={values}
          getInputOptionsAndLabel={getInputOptionsAndLabel}
        />
      )}

      {!submitConfirmationDialogState && (
        <FormikModal
          title={
            <FormattedMessage
              id={
                isEdit ? TranslationKeys.employees_actions_editSchedule : TranslationKeys.employees_actions_addSchedule
              }
            />
          }
          show={open}
          maxWidth={'md'}
          onSubmit={onClickSubmit}
          onCancel={isEdit ? closeDialog : undefined}
          onHide={() => {
            closeDialog();
            resetForm();
          }}
          disableEnforceFocus
        >
          {isEdit && (
            <Typography variant="h6" paragraph>
              {format.date(initialValues.startDate)}
            </Typography>
          )}

          <Grid container spacing={2}>
            <Grid item xs={4}>
              <InputFieldFactory
                field={{
                  type: 'date',
                  header: EmployeeEmploymentScheduleFields.StartDate.Label,
                  name: EmployeeEmploymentScheduleFields.StartDate.InputName,
                  options: EmployeeEmploymentScheduleFields.StartDate.Options,
                  required: true,
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <InputFieldFactory
                field={{
                  type: 'date',
                  header: EmployeeEmploymentScheduleFields.EndDate.Label,
                  name: EmployeeEmploymentScheduleFields.EndDate.InputName,
                  options: EmployeeEmploymentScheduleFields.EndDate.Options,
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <InputFieldFactory
                field={{
                  header: EmployeeEmploymentScheduleFields.PartTimePercentage.Label,
                  name: EmployeeEmploymentScheduleFields.PartTimePercentage.InputName,
                  options: EmployeeEmploymentScheduleFields.PartTimePercentage.Options,
                  type: 'number',
                  required: true,
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <InputFieldFactory
                field={{
                  header: EmployeeEmploymentScheduleFields.StartAveragePartTimePercentage.Label,
                  name: EmployeeEmploymentScheduleFields.StartAveragePartTimePercentage.InputName,
                  options: EmployeeEmploymentScheduleFields.StartAveragePartTimePercentage.Options,
                  type: 'number',
                  required: false,
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <InputFieldFactory
                field={{
                  header: EmployeeEmploymentScheduleFields.FullTimeHoursPerWeek.Label,
                  name: EmployeeEmploymentScheduleFields.FullTimeHoursPerWeek.InputName,
                  options: EmployeeEmploymentScheduleFields.FullTimeHoursPerWeek.Options,
                  type: 'number',
                  required: true,
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <InputFieldFactory
                field={{
                  header: EmployeeEmploymentScheduleFields.HoursPerWeek.Label,
                  name: EmployeeEmploymentScheduleFields.HoursPerWeek.InputName,
                  options: EmployeeEmploymentScheduleFields.HoursPerWeek.Options,
                  type: 'number',
                  disabled: true,
                }}
              />
            </Grid>
          </Grid>
        </FormikModal>
      )}
    </>
  );
};
